import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Body5, Container, Place, Row } from '@ui';
import { INTERNAL_STORAGE_KEYS, InternalStorage } from '@utils/storage';
import { FullLogo } from '@assets/logos/FullLogo';
import { FlatButton } from '@ui/buttons';
import { signIn } from '@services/authentication';
import InputField from '@containers/form/InputField';
import LoadingSpinner from '@ui/LoadingSpinner';
import { getDataForCache, getRequiredData, handleAppTrackingInit } from '@utils/initialization';
import { useDispatch } from 'react-redux';
import { parseQueryString } from '@utils/queryString';
import { ROUTES } from '@constants/routes';
import { safeNextPath } from '@utils/safeNext';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { logNetRequest } from '@utils/usefulFuncs';
import { sendApiRequest } from '@services/api';
import { Modal } from '@ui';
import { CloseButton } from '@ui/buttons/CloseButton';

export const LogoMobileWrap = styled('div')`
  position: relative;
  top: 20px;
  padding: 0;
  display: none;
`;
export const ValidationWrap = styled('div')`
  color: ${({ theme }) => theme.themeColors.error} !important;
  font-size: 12px;
`;
export const FormWrap = styled('div')`
  position: relative;

  input {
    border: 1px solid ${({ theme }) => theme.themeColors.text} !important;
    border-radius: 5px !important;
  }
`;
export const LoadingCover = styled.div`
  width: 360px;
  z-index: 1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 100px 0 100px 0;
`;
export const SignSwitchLink = styled('div')`
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4em;
`;
export const ExtraOptions = styled('div')`
  position: relative;
  margin: -8px 0 0 0;
  padding: 0 0 10px 0;
  width: 100%;
  max-width: 460px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const SignInFormWrapper = styled.div`
  max-width: 360px;
`;
const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ModalTitle = styled.div`
  font-size: 24px;
  font-weight: 600;

  padding: 0 24px 0 0;
`;
const ModalBody = styled.div`
  padding: 0 4px;
`;
const ModalContent = styled.div`
  input {
    // max-width: 420px;
  }

  button {
    max-width: unset;
  }
`;
const ModalCloseButton = styled.div`
  svg {
    width: 18px;
    height: 18px;
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

export const VALIDATION_MESSAGES = {
  multiple: 'Please enter your email and password to sign in.',
  email: 'Please enter your email',
  password: 'Please enter your password.',
};

export const signInValidation = ({ email, password }) => {
  const validateStates = {
    email: email.trim() !== '',
    password: password.trim() !== '',
  };

  const failedFieldsLength = Object.values(validateStates).filter((value) => {
    return value === false;
  }).length;

  const failedValidation = failedFieldsLength > 0;

  if (!failedValidation) {
    return {
      isValid: true,
    };
  }

  if (failedValidation && failedFieldsLength > 1) {
    return {
      isValid: false,
      validationField: 'multiple',
    };
  }

  if (failedValidation && failedFieldsLength === 1) {
    const validationField = Object.entries(validateStates).filter(([key, value]) => {
      return value === false;
    })[0];

    const fieldName = validationField[0];

    return {
      isValid: false,
      validationField: fieldName,
    };
  }
};

const ResendConfWrap = styled.div`
  button {
    width: 72px;
    padding: 2px 8px 3px 8px;
  }
`;
const ResendConfirmationLink = ({ email }) => {
  const [sent, setSent] = React.useState(false);

  const resendConfirmationLink = async (e) => {
    e?.preventDefault();
    const form = {
      email,
    };
    const { status, data } = await sendApiRequest('POST', 'v1/resend_confirmation_email', form);
    logNetRequest(status, data);
    setSent(true);
  };
  return (
    <Container>
      <Body5 bold>Please check your email for a confirmation link.</Body5>

      <ResendConfWrap>
        <FlatButton disabled={sent} onClick={sent ? () => null : resendConfirmationLink}>
          {sent ? 'Sent' : 'Resend'}
        </FlatButton>
      </ResendConfWrap>
    </Container>
  );
};

export const SignInForm = ({ isPasswordVisible, initialEmail = '' }) => {
  const dispatch = useDispatch();
  const [wantsCaseStudies, setWantsCaseStudies] = React.useState(false);
  const [isSSOModalOpen, setIsSSOModalOpen] = React.useState(false);

  const [email, setEmail] = React.useState(initialEmail);
  const [password, setPassword] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState('1');

  const [validationMessage, setValidationMessage] = React.useState('');
  const [isSigningIn, setIsSigningIn] = React.useState(false);

  React.useEffect(() => {
    const query = parseQueryString(window.location.search);
    if (query['case-study']) {
      setWantsCaseStudies(true);
    }
  }, []);

  const showSsoModal = (e) => {
    e?.preventDefault();
    setIsSSOModalOpen(true);
  };

  const ssoUserSignUp = async (e) => {
    e?.preventDefault();
    const URL = `v1/auth/saml/init?email=${email}`;
    const { status, data } = await sendApiRequest('GET', URL);

    logNetRequest(status, data);
    if (status === 200) {
      window.location.href = data.redirect;
    } else {
      if (data?.error === 'You must create a Tornado account before you can sign in with company credentials.') {
        setValidationMessage('You must create a Tornado account before you can sign in with company credentials.');
      } else {
        setValidationMessage('Sign in with company credentials is not setup for your company.');
      }
      setIsSSOModalOpen(false);
    }
  };

  const userSignIn = async (e) => {
    e?.preventDefault();

    const validationState = signInValidation({
      email,
      password,
    });

    if (!validationState.isValid) {
      setIsSigningIn(false);
      if (VALIDATION_MESSAGES[validationState.validationField]) {
        setValidationMessage(VALIDATION_MESSAGES[validationState.validationField]);
      }
    }

    setIsSigningIn(true);

    const { isAuthed, errorMessage } = await signIn({
      email,
      password,
      rememberMe,
    });

    if (isAuthed) {
      // const timeStart = createTimeInstance().valueOf();
      const responses = await getRequiredData(dispatch);
      await handleAppTrackingInit(responses[0]);
      getDataForCache(dispatch);
      // const timeEnd = createTimeInstance().valueOf();

      // after this runs, router updates to authed routes and handles redirect
      if (wantsCaseStudies) {
        const url = ROUTES.selectCaseStudy;
        window.open(url, '_blank');
      }

      const nextParam = new URLSearchParams(window.location.search).get('next');
      const safeNext = safeNextPath(nextParam);
      if (safeNext) {
        window.location.assign(safeNext);
      }
    } else {
      setIsSigningIn(false);
      setValidationMessage(errorMessage);
    }
  };

  const clickRemember = (e) => {
    const newValue = rememberMe === '1' ? '' : '1';
    if (newValue === '') {
      InternalStorage.setItemInStorage(INTERNAL_STORAGE_KEYS.EMAIL, '');
    }
    setRememberMe(newValue);
  };

  const handleKeyUp = (e) => {
    if (e.keyCode === 13) {
      userSignIn(e);
    }
  };

  if (isSigningIn) {
    return (
      <LoadingCover>
        <LoadingSpinner text={'Signing In...'} />
      </LoadingCover>
    );
  }

  return (
    <SignInFormWrapper onKeyDown={(e) => handleKeyUp(e)}>
      {isSSOModalOpen && (
        <Modal onClose={() => setIsSSOModalOpen(false)}>
          <ModalBody>
            <ModalHeader>
              <ModalTitle>Sign In Using Company Credentials</ModalTitle>
              <ModalCloseButton>
                <CloseButton onClick={() => setIsSSOModalOpen(false)} />
              </ModalCloseButton>
            </ModalHeader>
            <ModalContent>
              <Container top={24} bottom={16}>
                <Body5 bold>Company Email:</Body5>
              </Container>
              <InputField
                id="email-field"
                name={'email'}
                placeholder={'Company Email'}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
              <Container top={16}>
                <FlatButton id="sso-sign-up-continue" fullWidth onClick={ssoUserSignUp}>
                  Continue
                </FlatButton>
              </Container>
            </ModalContent>
          </ModalBody>
        </Modal>
      )}
      <FormWrap>
        <LogoMobileWrap>
          <FullLogo forceTornado />
        </LogoMobileWrap>
        {isPasswordVisible ? (
          <form>
            <InputField
              id="email-field"
              placeholder={'Email'}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            <InputField
              id="password-field"
              placeholder={'Password'}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            <ExtraOptions>
              <Row $position="relative" $vAlign="center">
                <input
                  type="checkbox"
                  id="checkbox"
                  checked={rememberMe === '1'}
                  onChange={clickRemember}
                  style={{ cursor: 'pointer', marginTop: 0 }}
                />
                <label htmlFor="checkbox" className="not-selectable" style={{ cursor: 'pointer', margin: 0 }}>
                  <Place $padding="0 0 0 8px" $position="relative" $top="0px" $display="inline-block">
                    Stay signed in
                  </Place>
                </label>
              </Row>
              <Place>
                <Link to="/forgot-password">Forgot Password</Link>
              </Place>
            </ExtraOptions>
            <Container bottom={8}>
              {validationMessage ? (
                validationMessage === 'Please check your email for a confirmation link.' ? (
                  <ResendConfirmationLink email={email} />
                ) : (
                  <ValidationWrap>{validationMessage}</ValidationWrap>
                )
              ) : null}
            </Container>
            <FlatButton id="sign-in-submit" fullWidth onClick={userSignIn}>
              Sign In
            </FlatButton>
            <Container top={12}>
              <SkeletonButton id="sso-sign-up-submit" fullWidth onClick={showSsoModal}>
                Sign In Using Company SSO
              </SkeletonButton>
            </Container>
            <Container top={8}>
              <SignSwitchLink>
                Don't have an account? <Link to="/signup">Sign Up</Link>
              </SignSwitchLink>
            </Container>
          </form>
        ) : (
          <>
            <Container top={4} bottom={8}>
              <FlatButton id="sso-sign-up-submit" fullWidth onClick={showSsoModal}>
                Sign In Using Company SSO
              </FlatButton>
            </Container>

            <Container top={8}>
              <SignSwitchLink>
                Don't have an account? <Link to="/signup">Sign Up</Link>
              </SignSwitchLink>
            </Container>
          </>
        )}
      </FormWrap>
    </SignInFormWrapper>
  );
};
